import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {App, Button, Card, Divider, Pagination, Segmented, Select, Space, Spin, Table, Tabs, Tag} from 'antd';
import {ArrowLeftOutlined, ClockCircleOutlined, CopyOutlined, DownOutlined, UpOutlined} from '@ant-design/icons';
import dayjs from 'dayjs';
import {AreaSeries, ColorType, createChart, LineSeries, Time} from 'lightweight-charts';
import {
    AiBacktestDecision,
    AiBacktestResult,
    aiBacktestService,
    AiBacktestTask,
    KlineDataItem
} from '../../services/aiBacktestService';
import LightweightCandlestickChart, {KLineBar} from '../../components/charts/LightweightCandlestickChart';
import {useAuth} from '../../contexts/AuthContext';
import SegmentEditor from '../../components/bot/SegmentEditor';
import {parsePromptToSegments, parseResponseToSegments} from '../../services/botService';
import MarkdownRenderer from '../../components/common/MarkdownRenderer';
import CompactIndicatorBar from '../../pages/trading/components/CompactIndicatorBar';
import {IndicatorConfig, TechnicalIndicator} from '../../hooks/useChartState';

const TIMEFRAMES = ['5m', '1H', '4H'];
const AVAILABLE_INDICATORS: TechnicalIndicator[] = ['EMA', 'RSI', 'BOLL', 'ADX', 'MACD'];
const DEFAULT_INDICATOR_CONFIGS: Record<TechnicalIndicator, IndicatorConfig> = {
    EMA: {period: 5, periods: [5, 20, 30], periodColors: ['var(--pt-blue)', '#00b4d8', '#0077b6']},
    RSI: {period: 14, periods: [5, 20, 30], periodColors: ['#9254de', '#d46b08', '#389e0d']},
    BOLL: {period: 20, periods: [20], stdDev: 2, stdDevs: [2.0], periodColors: ['#f5222d']},
    ADX: {period: 14, periods: [14], periodColors: ['var(--pt-amber)']},
    MACD: {period: 12, periods: [12, 26, 9], periodColors: ['#fa541c']},
};

const STATUS_COLORS: Record<string, string> = {
    RUNNING: 'processing', COMPLETED: 'success', FAILED: 'error', STOPPED: 'warning', PENDING: 'default'
};
const STATUS_LABELS: Record<string, string> = {
    RUNNING: '运行中', COMPLETED: '已完成', FAILED: '失败', STOPPED: '已停止', PENDING: '等待中'
};
const ACTION_COLORS: Record<string, string> = {
    BUY: 'green',
    SELL: 'red',
    HOLD: 'default',
    WATCH: 'blue',
    QUERY: 'blue',
    UNKNOWN: 'default'
};

function transformBacktestIndicators(rawIndicators: Record<string, any>): Record<string, any[]> | undefined {
    const result: Record<string, any[]> = {};
    Object.entries(rawIndicators).forEach(([indicatorType, indicatorList]: [string, any]) => {
        if (!Array.isArray(indicatorList)) return;
        indicatorList.forEach((indicator: any) => {
            const period = indicator.period;
            const values = indicator.values || [];
            if ('EMA' === indicatorType) {
                result[`EMA_ema_${period}`] = values
                    .filter((v: any) => v && null != v.timestamp)
                    .map((v: any) => ({time: v.timestamp, value: Number(v.value)}));
            } else if ('SMA' === indicatorType) {
                result[`SMA_sma_${period}`] = values
                    .filter((v: any) => v && null != v.timestamp)
                    .map((v: any) => ({time: v.timestamp, value: Number(v.value)}));
            } else if ('WMA' === indicatorType) {
                result[`WMA_wma_${period}`] = values
                    .filter((v: any) => v && null != v.timestamp)
                    .map((v: any) => ({time: v.timestamp, value: Number(v.value)}));
            } else if ('BOLL' === indicatorType) {
                const basePeriod = period.split(',')[0];
                const bollValues = indicator.bollValues || [];
                result[`BOLL_boll_${basePeriod}`] = bollValues
                    .filter((v: any) => v && null != v.timestamp)
                    .map((v: any) => ({
                        time: v.timestamp,
                        upper: Number(v.upper),
                        middle: Number(v.middle),
                        lower: Number(v.lower)
                    }));
            } else if ('MACD' === indicatorType) {
                const macdValues = indicator.macdValues || [];
                result[`MACD_macd_${period}`] = macdValues
                    .filter((v: any) => v && null != v.timestamp)
                    .map((v: any) => ({
                        time: v.timestamp,
                        diff: Number(v.diff),
                        signal: Number(v.dea),
                        histogram: Number(v.macd)
                    }));
            } else if ('RSI' === indicatorType) {
                result[`RSI_rsi_${period}`] = values
                    .filter((v: any) => v && null != v.timestamp)
                    .map((v: any) => ({time: v.timestamp, value: Number(v.value)}));
            } else if ('KDJ' === indicatorType) {
                const kdjValues = indicator.kdjValues || [];
                result[`KDJ_kdj_${period}`] = kdjValues
                    .filter((v: any) => v && null != v.timestamp)
                    .map((v: any) => ({time: v.timestamp, k: Number(v.k), d: Number(v.d), j: Number(v.j)}));
            } else if ('CCI' === indicatorType) {
                result[`CCI_cci_${period}`] = values
                    .filter((v: any) => v && null != v.timestamp)
                    .map((v: any) => ({time: v.timestamp, value: Number(v.value)}));
            } else if ('ATR' === indicatorType) {
                result[`ATR_atr_${period}`] = values
                    .filter((v: any) => v && null != v.timestamp)
                    .map((v: any) => ({time: v.timestamp, value: Number(v.value)}));
            } else if ('OBV' === indicatorType) {
                result[`OBV_obv_${period}`] = values
                    .filter((v: any) => v && null != v.timestamp)
                    .map((v: any) => ({time: v.timestamp, value: Number(v.value)}));
            } else if ('ADX' === indicatorType) {
                result[`ADX_adx_${period}`] = values
                    .filter((v: any) => v && null != v.timestamp)
                    .map((v: any) => ({time: v.timestamp, value: Number(v.value)}));
            }
        });
    });
    return Object.keys(result).length > 0 ? result : undefined;
}

const AiBacktestDetailPage: React.FC = () => {
    const {id} = useParams<{ id: string }>();
    const navigate = useNavigate();
    const {user} = useAuth();
    const userId = user?.id || 1;
    const taskId = Number(id);

    const [task, setTask] = useState<AiBacktestTask | null>(null);
    const [result, setResult] = useState<AiBacktestResult | null>(null);
    const [decisions, setDecisions] = useState<AiBacktestDecision[]>([]);
    const [decisionPage, setDecisionPage] = useState(0);
    const [decisionTotal, setDecisionTotal] = useState(0);
    const [decisionAction, setDecisionAction] = useState<string | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [decisionLoading, setDecisionLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('decisions');
    const [chartTab, setChartTab] = useState('equity');
    const [klineData, setKlineData] = useState<KLineBar[]>([]);
    const [klineLoading, setKlineLoading] = useState(false);
    const [chartTimeframe, setChartTimeframe] = useState('5m');
    const [selectedIndicators, setSelectedIndicators] = useState<TechnicalIndicator[]>(['EMA']);
    const [indicatorConfigs, setIndicatorConfigs] = useState<Record<TechnicalIndicator, IndicatorConfig>>(DEFAULT_INDICATOR_CONFIGS);
    const [indicatorData, setIndicatorData] = useState<Record<string, any[]> | undefined>(undefined);

    const activeIndicators = useMemo(() => {
        return selectedIndicators.filter(type => {
            const cfg = indicatorConfigs[type];
            const visible = cfg?.visiblePeriods;
            // 未设置 visiblePeriods 视为全部激活
            return undefined === visible || visible.length > 0;
        });
    }, [selectedIndicators, indicatorConfigs]);
    const [expandedDecisions, setExpandedDecisions] = useState<Set<number>>(new Set());
    const [decisionDetail, setDecisionDetail] = useState<Record<number, {
        promptTab: string;
        responseTab: string
    }>>({});
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstanceRef = useRef<any>(null);

    const fetchTaskAndResult = useCallback(async () => {
        setLoading(true);
        try {
            const taskRes = await aiBacktestService.getTasks(userId);
            const tasks = Array.isArray(taskRes.data?.data) ? taskRes.data.data : [];
            const found = tasks.find((t: AiBacktestTask) => t.id === taskId);
            if (found) setTask(found);

            const resRes = await aiBacktestService.getResult(taskId);
            if (resRes.data?.data) setResult(resRes.data.data);
        } catch (e) {
            console.error('获取回测详情失败', e);
        } finally {
            setLoading(false);
        }
    }, [taskId]);

    const fetchDecisions = useCallback(async () => {
        setDecisionLoading(true);
        try {
            const res = await aiBacktestService.getDecisions(taskId, decisionPage, 20, decisionAction);
            const data = res.data?.data;
            setDecisions(Array.isArray(data?.content) ? data.content : []);
            setDecisionTotal(data?.totalElements || 0);
        } catch (e) {
            console.error('获取决策日志失败', e);
        } finally {
            setDecisionLoading(false);
        }
    }, [taskId, decisionPage, decisionAction]);

    const fetchKlines = useCallback(async () => {
        setKlineLoading(true);
        try {
            const res = await aiBacktestService.getKlinesWithIndicators(taskId, chartTimeframe, activeIndicators);
            const data = res.data?.data;
            const items = data?.kline;
            if (Array.isArray(items)) {
                setKlineData(items.map((item: KlineDataItem) => ({
                    time: item.timestamp,
                    open: item.open,
                    high: item.high,
                    low: item.low,
                    close: item.close,
                    volume: item.volume,
                })));
            }
            const transformed = data?.indicators ? transformBacktestIndicators(data.indicators) : undefined;
            setIndicatorData(transformed || {});
        } catch (e) {
            console.error('获取K线数据失败', e);
        } finally {
            setKlineLoading(false);
        }
    }, [taskId, chartTimeframe, activeIndicators]);

    const toggleIndicator = useCallback((type: TechnicalIndicator) => {
        setSelectedIndicators(prev => {
            if (prev.includes(type)) {
                return prev.filter(t => t !== type);
            }
            return [...prev, type];
        });
    }, []);

    const handleUpdateIndicatorConfig = useCallback((type: TechnicalIndicator, config: Partial<IndicatorConfig>) => {
        setIndicatorConfigs(prev => ({...prev, [type]: {...prev[type], ...config}}));
    }, []);

    const visibleIndicators = useMemo(() => {
        const vis: Record<string, boolean> = {};
        if (indicatorData) {
            Object.keys(indicatorData).forEach(key => {
                const baseType = key.split('_')[0];
                const periodWithPrefix = key.substring(baseType.length + 1);
                const rawPeriod = periodWithPrefix.replace(/^(ema|sma|wma|rsi|boll|macd|kdj|cci|atr|obv|adx)_/i, '').replace(/,/g, '_');
                const visibleKey = `${baseType}_${rawPeriod}`;
                // 检查 visiblePeriods: 若已设置则仅展示勾选的周期
                const indType = baseType as TechnicalIndicator;
                const cfg = indicatorConfigs[indType];
                if (cfg?.visiblePeriods && cfg.visiblePeriods.length > 0) {
                    const periodNum = parseInt(rawPeriod.split('_')[0]);
                    if (!cfg.visiblePeriods.includes(periodNum)) return;
                }
                vis[visibleKey] = true;
            });
        }
        return vis;
    }, [indicatorData, indicatorConfigs]);

    useEffect(() => {
        fetchTaskAndResult();
    }, [fetchTaskAndResult]);

    useEffect(() => {
        if ('kline' === chartTab) {
            fetchKlines();
        }
    }, [chartTab, chartTimeframe, activeIndicators, fetchKlines]);

    useEffect(() => {
        if (activeTab === 'decisions') fetchDecisions();
    }, [fetchDecisions, activeTab]);

    useEffect(() => {
        const timer = setInterval(() => {
            if (task?.status === 'RUNNING') fetchTaskAndResult();
        }, 5000);
        return () => clearInterval(timer);
    }, [fetchTaskAndResult, task?.status]);

    useEffect(() => {
        if (!chartRef.current || !result?.equityCurveJson) return;
        if (chartInstanceRef.current) {
            chartInstanceRef.current.remove();
            chartInstanceRef.current = null;
        }

        try {
            const equityData = JSON.parse(result.equityCurveJson);
            if (!Array.isArray(equityData) || equityData.length === 0) return;

            const chart = createChart(chartRef.current, {
                width: chartRef.current.clientWidth,
                height: 320,
                layout: {
                    // canvas 绘制不解析 CSS 变量，图表颜色必须用具体色值
                    background: {type: ColorType.Solid, color: '#0d1117'},
                    textColor: '#7a93b0',
                    attributionLogo: false,
                },
                grid: {
                    vertLines: {color: 'rgba(255,255,255,0.05)'},
                    horzLines: {color: 'rgba(255,255,255,0.05)'},
                },
                crosshair: {mode: 1},
                localization: {
                    timeFormatter: (ts: number) => dayjs.unix(ts).format('YYYY-MM-DD HH:mm'),
                },
                timeScale: {
                    timeVisible: true,
                    borderColor: '#16202c',
                    tickMarkFormatter: (time: unknown) => {
                        if (typeof time === 'number') {
                            return dayjs.unix(time).format('MM-DD HH:mm');
                        }
                        return null;
                    },
                },
                rightPriceScale: {borderColor: '#16202c'},
                handleScroll: false,
                handleScale: false,
            });

            // 去重：lightweight-charts 要求时间严格递增，过滤连续重复时间戳
            const dedupedData = equityData.filter((p: any, i: number, arr: any[]) =>
                0 == i || p.time !== arr[i - 1].time
            );
            const hasBreakdown = dedupedData.length > 0 && null != dedupedData[0].unusedCapital;

            if (hasBreakdown) {
                // 底层：未使用资金（先添加，渲染在最下层）
                const unusedCapitalSeries = chart.addSeries(AreaSeries, {
                    lineColor: '#4d9fff',
                    topColor: 'rgba(59,130,246,0.35)',
                    bottomColor: 'rgba(59,130,246,0.05)',
                    lineWidth: 1,
                });
                unusedCapitalSeries.setData(dedupedData.map((p: any) => ({
                    time: (p.time / 1000) as Time,
                    value: Number(p.unusedCapital),
                })));

                // 顶层：总权益（后添加，填充覆盖底层上方 = 堆积效果，视觉差即为未结算盈亏）
                const equitySeries = chart.addSeries(AreaSeries, {
                    lineColor: '#00d97e',
                    topColor: 'rgba(0,200,150,0.25)',
                    bottomColor: 'rgba(0,200,150,0.02)',
                    lineWidth: 2,
                });
                equitySeries.setData(dedupedData.map((p: any) => ({
                    time: (p.time / 1000) as Time,
                    value: Number(p.equity),
                })));
            } else {
                // 降级：旧数据无分层字段，仅显示总权益线
                const areaSeries = chart.addSeries(AreaSeries, {
                    lineColor: '#00d97e',
                    topColor: 'rgba(0,200,150,0.3)',
                    bottomColor: 'rgba(0,200,150,0.02)',
                    lineWidth: 2,
                });
                areaSeries.setData(dedupedData.map((p: any) => ({
                    time: (p.time / 1000) as Time,
                    value: Number(p.equity),
                })));
            }

            if (result.benchmarkCurveJson) {
                const benchData = JSON.parse(result.benchmarkCurveJson);
                if (Array.isArray(benchData) && benchData.length > 0) {
                    const btcSeries = chart.addSeries(LineSeries, {
                        color: '#F0B429', lineWidth: 1,
                    });
                    const dedupedBench = benchData.filter((p: any, i: number, arr: any[]) =>
                        0 == i || p.time !== arr[i - 1].time
                    );
                    btcSeries.setData(dedupedBench.map((p: any) => ({
                        time: (p.time / 1000) as Time,
                        value: Number(p.btcEquity || 0),
                    })));
                }
            }

            // 悬浮数据窗口
            const tooltipEl = document.createElement('div');
            tooltipEl.style.cssText = 'position:absolute;display:none;padding:8px 12px;background:var(--pt-bg-hover);border:1px solid var(--pt-border);border-radius:8px;pointer-events:none;z-index:10;font-size:12px;line-height:1.8;white-space:nowrap;';
            chartRef.current.appendChild(tooltipEl);

            chart.subscribeCrosshairMove((param: any) => {
                if (!param.point || !param.time) {
                    tooltipEl.style.display = 'none';
                    return;
                }
                const hoverTime = (param.time as number) * 1000;
                const point = dedupedData.find((p: any) => p.time === hoverTime);
                if (!point || !hasBreakdown) {
                    tooltipEl.style.display = 'none';
                    return;
                }
                const unusedCap = Number(point.unusedCapital || 0);
                const posValue = Number(point.positionValue || 0);
                const equity = Number(point.equity || 0);
                const posColor = posValue >= 0 ? 'var(--pt-bull)' : 'var(--pt-bear)';
                tooltipEl.innerHTML = [
                    `<span style="color:var(--pt-text-dim)">${dayjs(hoverTime).format('YYYY-MM-DD HH:mm')}</span>`,
                    `<span style="color:var(--pt-blue)">未使用资金 ${unusedCap.toFixed(0)} USDT</span>`,
                    `<span style="color:${posColor}">未结算盈亏 ${posValue >= 0 ? '+' : ''}${posValue.toFixed(0)} USDT</span>`,
                    `<span style="color:var(--pt-bull)">总权益 ${equity.toFixed(0)} USDT</span>`,
                ].join('<br/>');
                const rect = chartRef.current!.getBoundingClientRect();
                const x = param.point.x + 16;
                const y = param.point.y - 10;
                tooltipEl.style.left = Math.min(x, rect.width - 180) + 'px';
                tooltipEl.style.top = Math.max(y, 10) + 'px';
                tooltipEl.style.display = 'block';
            });

            chart.timeScale().fitContent();
            chartInstanceRef.current = chart;

            const resizeObserver = new ResizeObserver(() => {
                if (chartRef.current && chartInstanceRef.current) {
                    chartInstanceRef.current.applyOptions({width: chartRef.current.clientWidth});
                }
            });
            resizeObserver.observe(chartRef.current);
            return () => {
                resizeObserver.disconnect();
                if (tooltipEl.parentNode) tooltipEl.parentNode.removeChild(tooltipEl);
            };
        } catch (e) {
            console.error('渲染权益曲线失败', e);
        }
    }, [result?.equityCurveJson, result?.benchmarkCurveJson]);

    const toggleExpand = async (decisionId: number) => {
        const next = new Set(expandedDecisions);
        if (next.has(decisionId)) {
            next.delete(decisionId);
        } else {
            next.add(decisionId);
            if (!decisionDetail[decisionId]) {
                try {
                    const res = await aiBacktestService.getDecisionDetail(taskId, decisionId);
                    const d = res.data?.data;
                    if (d) {
                        setDecisionDetail(prev => ({
                            ...prev,
                            [decisionId]: {promptTab: 'segments', responseTab: 'segments'}
                        }));
                        setDecisions(prev => prev.map(item =>
                            item.id === decisionId ? {
                                ...item,
                                promptContent: d.promptContent,
                                responseContent: d.responseContent
                            } : item
                        ));
                    }
                } catch (e) {
                    console.error('获取决策详情失败', e);
                }
            }
        }
        setExpandedDecisions(next);
    };

    const formatMetric = (val: number | undefined, suffix = '%', decimals = 2) => {
        if (val === undefined || val === null) return '-';
        const n = Number(val);
        const color = n > 0 ? 'var(--pt-bull)' : n < 0 ? 'var(--pt-bear)' : 'var(--pt-text-primary)';
        return <span style={{color}}>{n.toFixed(decimals)}{suffix}</span>;
    };

    const tradeColumns = [
        {title: '时间', dataIndex: 'time', key: 'time', render: (v: number) => v ? formatTs(v) : '-'},
        {
            title: '合约', dataIndex: 'instId', key: 'instId', width: 120,
            render: (v: string, record: any) => <span
                style={{color: record.posSide === 'short' ? 'var(--pt-bear)' : 'var(--pt-bull)'}}>{v}</span>
        },
        {
            title: '开仓/平仓', dataIndex: 'openClose', key: 'openClose', width: 70,
            render: (v: string) => v === 'open' ? <Tag color="green">开仓</Tag> : v === 'close' ?
                <Tag color="red">平仓</Tag> : '-'
        },
        {
            title: '开仓均价', dataIndex: 'avgPrice', key: 'avgPrice', width: 100,
            render: (v: number) => v != null ? v.toFixed(4) : '-'
        },
        {title: '成交价', dataIndex: 'price', key: 'price', width: 100},
        {title: '数量', dataIndex: 'quantity', key: 'quantity', width: 100},
        {title: '价值', dataIndex: 'value', key: 'value', width: 100},
        {
            title: '盈亏', dataIndex: 'pnl', key: 'pnl', width: 90,
            render: (v: number) => v != null ?
                <span style={{color: v >= 0 ? 'var(--pt-bull)' : 'var(--pt-bear)'}}>{v.toFixed(4)}</span> : '-'
        },
        {
            title: '止盈/止损', dataIndex: 'tpSl', key: 'tpSl', width: 70,
            render: (v: string) => v === 'TP' ? <Tag color="green">止盈</Tag> : v === 'SL' ?
                <Tag color="red">止损</Tag> : '-'
        },
        {
            title: '主动/被动', dataIndex: 'source', key: 'source', width: 70,
            render: (v: string) => v === 'AI' ? <Tag color="blue">主动</Tag> : v === 'TP_SL' ?
                <Tag color="orange">被动</Tag> : v === 'AI_ERROR' ? <Tag color="red">失败</Tag> : '-'
        },
    ];

    const settlementColumns = [
        {title: '结算时间', dataIndex: 'time', key: 'time', width: 160, render: (v: number) => v ? formatTs(v) : '-'},
        {
            title: '总权益', dataIndex: 'equity', key: 'equity', width: 120,
            render: (v: number) => <span style={{color: 'var(--pt-bull)'}}>{Number(v).toFixed(0)}</span>
        },
        {
            title: '未使用资金', dataIndex: 'unusedCapital', key: 'unusedCapital', width: 120,
            render: (v: number) => <span style={{color: 'var(--pt-blue)'}}>{Number(v).toFixed(0)}</span>
        },
        {
            title: '冻结资金(保证金)', dataIndex: 'margin', key: 'margin', width: 130,
            render: (v: number) => null != v ? Number(v).toFixed(0) : '-'
        },
        {
            title: '未结算盈亏', dataIndex: 'unrealizedPnl', key: 'unrealizedPnl', width: 120,
            render: (v: number) => {
                if (null == v) return '-';
                const n = Number(v);
                return <span style={{color: n >= 0 ? 'var(--pt-bull)' : 'var(--pt-bear)'}}>
                    {n >= 0 ? '+' : ''}{n.toFixed(0)}
                </span>;
            }
        },
        {
            title: '手续费', dataIndex: 'cumTradeFee', key: 'cumTradeFee', width: 100,
            render: (v: number) => null != v ? Number(v).toFixed(2) : '0'
        },
        {
            title: '资金费', dataIndex: 'cumFundingFee', key: 'cumFundingFee', width: 100,
            render: (v: number) => null != v ? Number(v).toFixed(2) : '0'
        },
    ];

    const positionColumns = [
        {
            title: '品种', dataIndex: 'symbol', key: 'symbol', width: 100,
        },
        {
            title: '方向', dataIndex: 'side', key: 'side', width: 60,
            render: (v: string) => 'long' === v ? <Tag color="green">多</Tag> : <Tag color="red">空</Tag>
        },
        {
            title: '开仓时间', dataIndex: 'openTime', key: 'openTime', width: 160,
            render: (v: number) => v ? formatTs(v) : '-'
        },
        {
            title: '平仓时间', dataIndex: 'closeTime', key: 'closeTime', width: 160,
            render: (v: number) => v ? formatTs(v) : '-'
        },
        {
            title: '开仓价格', dataIndex: 'openPrice', key: 'openPrice', width: 100,
            render: (v: number) => null != v ? Number(v).toFixed(4) : '-'
        },
        {
            title: '平仓价格', dataIndex: 'closePrice', key: 'closePrice', width: 100,
            render: (v: number) => null != v ? Number(v).toFixed(4) : '-'
        },
        {
            title: '数量(张)', dataIndex: 'quantity', key: 'quantity', width: 80,
            render: (v: number) => null != v ? Number(v).toFixed(1) : '-'
        },
        {
            title: '盈亏', dataIndex: 'realizedPnl', key: 'realizedPnl', width: 100,
            render: (v: number) => {
                if (null == v) return '-';
                const n = Number(v);
                return <span style={{color: n >= 0 ? 'var(--pt-bull)' : 'var(--pt-bear)'}}>
                    {n >= 0 ? '+' : ''}{n.toFixed(4)}
                </span>;
            }
        },
    ];

    if (loading && !task) {
        return <div style={{padding: 40, textAlign: 'center'}}><Spin size="large"/></div>;
    }
    if (!task) {
        return <div style={{padding: 40, textAlign: 'center', color: 'var(--pt-text-dim)'}}>任务不存在</div>;
    }

    return (
        <div style={{padding: 20, maxWidth: 1200, margin: '0 auto'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16}}>
                <Button icon={<ArrowLeftOutlined/>} onClick={() => navigate('/ai-backtest')} type="text">
                    返回
                </Button>
                <h3 style={{color: 'var(--pt-text-primary)', margin: 0}}>{task.taskName}</h3>
                <Tag color={STATUS_COLORS[task.status] || 'default'}>{STATUS_LABELS[task.status] || task.status}</Tag>
                {task.status === 'RUNNING' && <Tag>进度 {Math.round(task.progress || 0)}%</Tag>}
            </div>

            {result && (
                <>
                    <div style={{display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 16}}>
                        <MetricCard label="总收益率" value={formatMetric(result.totalReturnPct)}/>
                        <MetricCard label="最大回撤" value={formatMetric(result.maxDrawdownPct)}/>
                        <MetricCard label="夏普比率" value={<span
                            style={{color: 'var(--pt-purple)'}}>{result.sharpeRatio ? Number(result.sharpeRatio).toFixed(2) : '-'}</span>}/>
                        <MetricCard label="胜率" value={formatMetric(result.winRate)}/>
                        <MetricCard label="交易次数"
                                    value={<span
                                        style={{color: 'var(--pt-text-primary)'}}>{result.totalTrades || 0}</span>}/>
                    </div>
                    <div style={{display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 16}}>
                        <MetricCard label="跑赢BTC基准" value={formatMetric(result.btcBenchmarkPct)}/>
                        <MetricCard label="盈亏比" value={result.profitLossRatio ?
                            <span
                                style={{color: 'var(--pt-text-primary)'}}>{Number(result.profitLossRatio).toFixed(2)}</span> :
                            <span style={{color: 'var(--pt-text-primary)'}}>-</span>}/>
                        <MetricCard label="最大连亏" value={<span
                            style={{color: 'var(--pt-bear)'}}>{result.maxConsecutiveLoss || 0}次</span>}/>
                        <MetricCard label="决策总数"
                                    value={<span
                                        style={{color: 'var(--pt-text-primary)'}}>{result.totalDecisions || 0}</span>}/>
                        <MetricCard label="最终余额" value={<span
                            style={{color: 'var(--pt-text-primary)'}}>{result.finalBalance ? Number(result.finalBalance).toFixed(0) : '-'} USDT</span>}/>
                    </div>
                </>
            )}

            <Card size="small"
                  style={{marginBottom: 16, background: 'var(--pt-bg-panel)', borderColor: 'var(--pt-border-subtle)'}}>
                <Tabs activeKey={chartTab} onChange={(key) => {
                    setChartTab(key);
                    if ('kline' === key) fetchKlines();
                }} items={[
                    {
                        key: 'equity',
                        label: '权益走势',
                        children: <div ref={chartRef} style={{width: '100%', height: 320}}/>
                    },
                    {
                        key: 'kline',
                        label: '数据',
                        children: (
                            <div>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                    marginBottom: 12,
                                    flexWrap: 'wrap'
                                }}>
                                    <Segmented
                                        size="small"
                                        value={chartTimeframe}
                                        options={TIMEFRAMES.map(tf => ({label: tf, value: tf}))}
                                        onChange={(v) => setChartTimeframe(v as string)}
                                    />
                                    <div style={{width: 1, height: 20, background: 'var(--pt-border-subtle)'}}/>
                                    <CompactIndicatorBar
                                        selectedIndicators={selectedIndicators}
                                        indicatorConfigs={indicatorConfigs}
                                        onToggleIndicator={toggleIndicator}
                                        onUpdateIndicatorConfig={handleUpdateIndicatorConfig}
                                        availableTypes={AVAILABLE_INDICATORS}
                                        hideActions
                                    />
                                </div>
                                {klineLoading ? (
                                    <div style={{textAlign: 'center', padding: 40}}><Spin/></div>
                                ) : klineData.length > 0 ? (
                                    <LightweightCandlestickChart
                                        data={klineData}
                                        height={500}
                                        indicators={indicatorData}
                                        visibleIndicators={visibleIndicators}
                                        timeFrame={chartTimeframe.toLowerCase()}
                                        disableHorizontalZoom
                                        lockRightEdge
                                    />
                                ) : (
                                    <div style={{
                                        textAlign: 'center',
                                        padding: 40,
                                        color: 'var(--pt-text-dim)'
                                    }}>暂无K线数据</div>
                                )}
                            </div>
                        )
                    },
                ]}/>
            </Card>

            <Card size="small" style={{background: 'var(--pt-bg-panel)', borderColor: 'var(--pt-border-subtle)'}}>
                <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
                    {
                        key: 'decisions',
                        label: `AI决策日志 (${decisionTotal})`,
                        children: (
                            <div style={{display: 'flex', flexDirection: 'column', minHeight: 0}}>
                                <div style={{display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap'}}>
                                    <Select
                                        allowClear
                                        placeholder="筛选动作"
                                        style={{width: 120}}
                                        value={decisionAction}
                                        onChange={(v) => {
                                            setDecisionAction(v);
                                            setDecisionPage(0);
                                        }}
                                        options={[
                                            {value: 'BUY', label: 'BUY'},
                                            {value: 'SELL', label: 'SELL'},
                                            {value: 'HOLD', label: 'HOLD'},
                                            {value: 'QUERY', label: 'QUERY'},
                                        ]}
                                    />
                                </div>

                                <div style={{flex: 1, overflowY: 'auto', paddingRight: 4, minHeight: 0}}>
                                    {decisionLoading ? (
                                        <div style={{textAlign: 'center', padding: 40}}><Spin/></div>
                                    ) : decisions.length === 0 ? (
                                        <div style={{
                                            textAlign: 'center',
                                            color: 'var(--pt-text-dim)',
                                            padding: 40
                                        }}>暂无决策记录</div>
                                    ) : (
                                        decisions.map((decision, i) => (
                                            <DecisionCard
                                                key={decision.id}
                                                record={decision}
                                                index={decisionPage * 20 + i + 1}
                                                expanded={expandedDecisions.has(decision.id)}
                                                detail={decisionDetail[decision.id]}
                                                onToggle={() => toggleExpand(decision.id)}
                                            />
                                        ))
                                    )}
                                </div>

                                {decisionTotal > 0 && (
                                    <div style={{
                                        marginTop: 16,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        paddingTop: 16,
                                        borderTop: '1px solid var(--pt-border)'
                                    }}>
                                        <Pagination
                                            current={decisionPage + 1}
                                            total={decisionTotal}
                                            pageSize={20}
                                            onChange={(p) => setDecisionPage(p - 1)}
                                            showTotal={(t) => `第 ${decisionPage * 20 + 1}-${Math.min((decisionPage + 1) * 20, t)} 条，共 ${t} 条记录`}
                                            showSizeChanger={false}
                                        />
                                    </div>
                                )}
                            </div>
                        )
                    },
                    {
                        key: 'trades',
                        label: '交易明细',
                        children: result?.tradesJson ? (
                            <Table
                                dataSource={JSON.parse(result.tradesJson)}
                                columns={tradeColumns}
                                rowKey={(_, i) => String(i)}
                                size="small"
                                pagination={{pageSize: 20}}
                            />
                        ) : (
                            <div style={{
                                textAlign: 'center',
                                color: 'var(--pt-text-dim)',
                                padding: 40
                            }}>暂无交易记录</div>
                        )
                    },
                    {
                        key: 'settlements',
                        label: '结算明细',
                        children: result?.equityCurveJson ? (
                            <Table
                                dataSource={JSON.parse(result.equityCurveJson)}
                                columns={settlementColumns}
                                rowKey={(_, i) => String(i)}
                                size="small"
                                pagination={{pageSize: 20}}
                            />
                        ) : (
                            <div style={{
                                textAlign: 'center',
                                color: 'var(--pt-text-dim)',
                                padding: 40
                            }}>暂无结算记录</div>
                        )
                    },
                    {
                        key: 'positions',
                        label: '仓位明细',
                        children: result?.positionRecordsJson ? (
                            <Table
                                dataSource={JSON.parse(result.positionRecordsJson)}
                                columns={positionColumns}
                                rowKey={(_, i) => String(i)}
                                size="small"
                                pagination={{pageSize: 20}}
                            />
                        ) : (
                            <div style={{
                                textAlign: 'center',
                                color: 'var(--pt-text-dim)',
                                padding: 40
                            }}>暂无仓位记录</div>
                        )
                    },
                ]}/>
            </Card>
        </div>
    );
};

const MetricCard: React.FC<{ label: string; value: React.ReactNode }> = ({label, value}) => (
    <Card size="small"
          style={{background: 'var(--pt-bg-panel)', borderColor: 'var(--pt-border-subtle)', textAlign: 'center'}}>
        <div style={{color: 'var(--pt-text-dim)', fontSize: 12, marginBottom: 4}}>{label}</div>
        <div style={{fontSize: 18, fontFamily: 'IBM Plex Mono, monospace'}}>{value}</div>
    </Card>
);

const DecisionCard: React.FC<{
    record: AiBacktestDecision;
    index: number;
    expanded: boolean;
    detail?: { promptTab: string; responseTab: string };
    onToggle: () => void;
}> = ({record, index, expanded, detail, onToggle}) => {
    const {message: messageApi} = App.useApp();
    const [promptTab, setPromptTab] = useState(detail?.promptTab || 'segments');
    const [responseTab, setResponseTab] = useState(detail?.responseTab || 'segments');

    const promptSegments = React.useMemo(() =>
        parsePromptToSegments(record.promptContent || ''), [record.promptContent]);
    const responseSegments = React.useMemo(() =>
        parseResponseToSegments(record.responseContent || ''), [record.responseContent]);

    const copyText = (text: string) => {
        navigator.clipboard.writeText(text).then(() => messageApi.success('已复制'));
    };

    const renderContent = (content: string, segments: ReturnType<typeof parsePromptToSegments>, tab: string, setTab: (v: string) => void) => (
        <div style={{width: '100%', maxWidth: '100%', minWidth: 0, overflow: 'hidden'}}>
            <Space style={{marginBottom: 8}}>
                <Button size="small" type={tab === 'segments' ? 'primary' : 'default'}
                        onClick={() => setTab('segments')}>分章节</Button>
                <Button size="small" type={tab === 'raw' ? 'primary' : 'default'}
                        onClick={() => setTab('raw')}>原始文本</Button>
                <Button size="small" icon={<CopyOutlined/>}
                        onClick={() => copyText(content || '')}>复制</Button>
            </Space>
            {tab === 'segments' && segments.length > 0 ? (
                <SegmentEditor segments={segments} readOnly={true}/>
            ) : tab === 'raw' ? (
                <div style={{
                    background: 'var(--pt-bg-hover)', padding: 12, borderRadius: 8,
                    maxHeight: 400, overflow: 'auto',
                    width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box',
                }}>
                    <pre style={{
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        color: 'var(--pt-text-primary)', fontSize: 13,
                        fontFamily: 'IBM Plex Mono, Monaco, Consolas, monospace',
                        margin: 0,
                    }}>
                        {content || '(无内容)'}
                    </pre>
                </div>
            ) : (
                <div style={{
                    background: 'var(--pt-bg-hover)', padding: 12, borderRadius: 8,
                    maxHeight: 400, overflow: 'auto',
                    width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box',
                }}>
                    <MarkdownRenderer content={content || '(无内容)'}/>
                </div>
            )}
        </div>
    );

    return (
        <Card
            size="small"
            style={{
                backgroundColor: 'var(--pt-bg-panel)',
                border: '1px solid var(--pt-border)',
                marginBottom: '12px',
                borderRadius: '6px'
            }}
            styles={{body: {padding: '12px'}}}
        >
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <Space size="middle" wrap>
                    <span style={{
                        color: 'var(--pt-text-dim)',
                        fontSize: 12,
                        fontFamily: 'IBM Plex Mono, monospace'
                    }}>#{index}</span>
                    <span style={{color: 'var(--pt-text-primary)', fontSize: 13}}>
                        <ClockCircleOutlined style={{marginRight: 4, color: 'var(--pt-text-dim)'}}/>
                        {formatTs(record.decisionTime)}
                    </span>
                    <Tag color={ACTION_COLORS[record.action] || 'default'}>{record.action}</Tag>
                    {record.price != null &&
                        <span style={{color: 'var(--pt-text-secondary)', fontSize: 13}}>价格: {record.price}</span>}
                    {record.confidence != null &&
                        <span style={{
                            color: 'var(--pt-text-secondary)',
                            fontSize: 13
                        }}>置信度: {record.confidence}</span>}
                </Space>
                <Button
                    size="small"
                    type="text"
                    icon={expanded ? <UpOutlined/> : <DownOutlined/>}
                    onClick={onToggle}
                >
                    {expanded ? '收起' : '展开'}
                </Button>
            </div>

            {expanded && (
                <>
                    <Divider style={{margin: '8px 0', borderColor: 'var(--pt-border-subtle)'}}/>
                    <Tabs defaultActiveKey="prompt" size="small" items={[
                        {
                            key: 'prompt',
                            label: 'Prompt内容',
                            children: renderContent(record.promptContent || '', promptSegments, promptTab, setPromptTab)
                        },
                        {
                            key: 'response',
                            label: '模型响应',
                            children: renderContent(record.responseContent || '', responseSegments, responseTab, setResponseTab)
                        },
                    ]}/>
                </>
            )}
        </Card>
    );
};

function formatTs(ts: number) {
    if (!ts) return '-';
    return dayjs(ts).format('YYYY-MM-DD HH:mm');
}

export default AiBacktestDetailPage;
